import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Skeleton,
  Button,
  useTheme,
} from '@mui/material';
import {
  Inventory,
  ShoppingCart,
  LocalShipping,
  Warning,
  Category,
  People,
  Assignment,
  NewReleases,
  ReceiptLong,
  ArrowForward,
} from '@mui/icons-material';
import { useGetDashboardSummaryQuery } from '../../../store/api/dashboardApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { usePermission } from '../../../hooks/usePermission';

function formatAmount(value, currency = 'USD') {
  const num = Number(value || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(num);
  } catch {
    return `${currency} ${num.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
  }
}

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasModuleAccess } = usePermission();
  const canPurchases = hasModuleAccess('purchases');
  const canSales = hasModuleAccess('sales');
  const canProducts = hasModuleAccess('products');
  const canInventory = hasModuleAccess('inventory');
  const canCustomers = hasModuleAccess('sales');

  const { data, isLoading, error } = useGetDashboardSummaryQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = data?.currency || company?.currency || 'USD';

  const [salesFilter, setSalesFilter] = useState('all'); // 'all', 'month', 'today'
  const [salesCardFilter, setSalesCardFilter] = useState('today'); // 'today', 'month', 'all'

  const summary = data?.summary || {};
  const lowStockItems = data?.lowStockItems || [];
  const recentActivity = data?.recentActivity || [];

  let salesValue = summary.totalSales;
  let salesSub = summary.salesCount != null ? `${summary.salesCount} orders` : null;
  let salesTitle = 'Total Sales';

  if (salesFilter === 'month') {
    salesValue = summary.salesThisMonth;
    salesSub = summary.salesCountThisMonth != null ? `${summary.salesCountThisMonth} orders` : null;
    salesTitle = 'Total Sales (Month)';
  } else if (salesFilter === 'today') {
    salesValue = summary.salesToday;
    salesSub = summary.salesCountToday != null ? `${summary.salesCountToday} orders` : null;
    salesTitle = 'Total Sales (Today)';
  }

  const statCards = [
    {
      title: 'Total Products',
      value: summary.totalProducts ?? '—',
      sub: summary.activeProducts != null ? `${summary.activeProducts} active` : null,
      icon: <Inventory sx={{ fontSize: 36 }} />,
      color: theme.palette.primary.main,
      path: canProducts ? '/products' : null,
    },
    {
      title: salesTitle,
      value: formatAmount(salesValue, currency),
      sub: salesSub,
      icon: <ShoppingCart sx={{ fontSize: 36 }} />,
      color: theme.palette.success.main,
      path: canSales ? '/sales' : null,
      isSalesCard: true,
    },
    {
      title: 'Total Purchases',
      value: formatAmount(summary.totalPurchases, currency),
      sub: summary.purchaseCount != null ? `${summary.purchaseCount} orders` : null,
      icon: <LocalShipping sx={{ fontSize: 36 }} />,
      color: theme.palette.warning.main,
      path: canPurchases ? '/purchases' : null,
    },
    {
      title: 'Low Stock Items',
      value: summary.lowStockCount ?? 0,
      sub: summary.lowStockCount > 0 ? 'Need attention' : null,
      icon: <Warning sx={{ fontSize: 36 }} />,
      color: theme.palette.error.main,
      path: canInventory ? '/inventory' : null,
    },
  ];

  const quickActions = [
    { label: 'Add Product', icon: <Inventory />, path: canProducts ? '/products/new' : null },
    { label: 'New Sale', icon: <ShoppingCart />, path: canSales ? '/sales/orders/new' : null },
    { label: 'Purchase Order', icon: <LocalShipping />, path: canPurchases ? '/purchases/orders/new' : null },
    { label: 'Categories', icon: <Category />, path: canProducts ? '/products/categories' : null },
    { label: 'Inventory', icon: <Inventory />, path: canInventory ? '/inventory' : null },
    { label: 'Customers', icon: <People />, path: canCustomers ? '/customers' : null },
  ].filter((a) => a.path);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load dashboard. Please try again.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Dashboard
        </Typography>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((stat, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card
              onClick={stat.path ? () => navigate(stat.path) : undefined}
              sx={{
                height: '100%',
                cursor: stat.path ? 'pointer' : 'default',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': stat.path
                  ? {
                      transform: 'translateY(-2px)',
                      boxShadow: 2,
                    }
                  : {},
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', p: 2.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {stat.title}
                  </Typography>
                  {isLoading ? (
                    <Skeleton variant="text" width={120} height={36} />
                  ) : (
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {stat.value}
                    </Typography>
                  )}
                  {stat.sub && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {stat.sub}
                    </Typography>
                  )}
                  {stat.isSalesCard && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }} onClick={(e) => e.stopPropagation()}>
                      {['today', 'month', 'all'].map((filter) => (
                        <Chip
                          key={filter}
                          label={filter === 'all' ? 'All' : filter === 'month' ? 'Month' : 'Today'}
                          size="small"
                          color={salesFilter === filter ? 'success' : 'default'}
                          variant={salesFilter === filter ? 'filled' : 'outlined'}
                          onClick={() => setSalesFilter(filter)}
                          sx={{
                            fontSize: '0.65rem',
                            height: 18,
                            cursor: 'pointer',
                            fontWeight: 600,
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${stat.color}20`,
                    color: stat.color,
                  }}
                >
                  {stat.icon}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={1.5}>
              {quickActions.map((action, index) => (
                <Grid size={{ xs: 6, sm: 4 }} key={index}>
                  <Card
                    onClick={() => action.path && navigate(action.path)}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        transform: 'translateY(-2px)',
                        boxShadow: 1,
                      },
                    }}
                  >
                    <Box sx={{ color: 'primary.main', mb: 0.5 }}>{action.icon}</Box>
                    <Typography variant="body2" fontWeight={600}>
                      {action.label}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Sales This Month */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {salesCardFilter === 'all' ? 'Sales All Time' : salesCardFilter === 'month' ? 'Sales This Month' : 'Sales Today'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {['today', 'month', 'all'].map((filter) => (
                  <Chip
                    key={filter}
                    label={filter === 'all' ? 'All' : filter === 'month' ? 'Month' : 'Today'}
                    size="small"
                    color={salesCardFilter === filter ? 'success' : 'default'}
                    variant={salesCardFilter === filter ? 'filled' : 'outlined'}
                    onClick={() => setSalesCardFilter(filter)}
                    sx={{
                      fontSize: '0.65rem',
                      height: 18,
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            </Box>
            {isLoading ? (
              <Skeleton variant="text" width="80%" height={48} sx={{ my: 2 }} />
            ) : (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {formatAmount(
                    salesCardFilter === 'all' ? summary.totalSales : salesCardFilter === 'month' ? summary.salesThisMonth : summary.salesToday,
                    currency
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(salesCardFilter === 'all' ? summary.salesCount : salesCardFilter === 'month' ? summary.salesCountThisMonth : summary.salesCountToday) ?? 0} order(s)
                </Typography>
                {canSales && (
                  <Button
                    size="small"
                    startIcon={<ArrowForward />}
                    onClick={() => navigate('/sales')}
                    sx={{ mt: 2 }}
                  >
                    View Sales
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* New Features */}
        {canPurchases && (
          <Grid size={{ xs: 12 }}>
            <Paper
              sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: 'success.50',
                border: '1px solid',
                borderColor: 'success.200',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <NewReleases sx={{ color: 'success.main', fontSize: 24 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  New Features
                </Typography>
              </Box>
              <Card
                onClick={() => navigate('/purchases/orders/new?lpo=1')}
                sx={{
                  display: 'inline-flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  cursor: 'pointer',
                  maxWidth: 320,
                  '&:hover': { bgcolor: 'success.100', boxShadow: 1 },
                }}
              >
                <Assignment sx={{ color: 'success.main', fontSize: 32 }} />
                <Box>
                  <Typography fontWeight={600}>Create LPO</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Local Purchase Order with reference number
                  </Typography>
                </Box>
              </Card>
            </Paper>
          </Grid>
        )}

        {/* Recent Activity */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Recent Activity
            </Typography>
            {isLoading ? (
              [1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />)
            ) : recentActivity.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No recent activity
              </Typography>
            ) : (
              <List disablePadding>
                {recentActivity.slice(0, 8).map((item, index) => (
                  <ListItem
                    key={`${item.type}-${item.id}-${index}`}
                    disablePadding
                    sx={{
                      py: 1,
                      borderBottom: index < 7 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {item.type === 'sale' ? (
                        <ReceiptLong sx={{ color: 'success.main', fontSize: 20 }} />
                      ) : (
                        <LocalShipping sx={{ color: 'warning.main', fontSize: 20 }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {item.description} · {formatAmount(item.amount, currency)}
                        </Typography>
                      }
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(item.date)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Low Stock */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Low Stock
              </Typography>
              {lowStockItems.length > 0 && canInventory && (
                <Button size="small" onClick={() => navigate('/inventory')}>
                  View all
                </Button>
              )}
            </Box>
            {isLoading ? (
              [1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />)
            ) : lowStockItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No low stock items
              </Typography>
            ) : (
              <List disablePadding>
                {lowStockItems.slice(0, 6).map((item, index) => (
                  <ListItem
                    key={`${item.productId}-${item.locationName}-${index}`}
                    disablePadding
                    sx={{
                      py: 1.25,
                      borderBottom: index < 5 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Warning sx={{ color: 'warning.main', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.productName}
                      secondary={`${item.locationName} · Qty: ${item.quantity} (reorder: ${item.reorderPoint})`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip label="Low" size="small" color="warning" variant="outlined" />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
